import React from 'react';

/**
 * 共用提醒設定編輯器
 * 統一視窗三塊：截止日期、關鍵日期提醒（單發）、定期催辦（分段頻率＋逾期追蹤）
 * 供「單一廠商編輯」與「批量設定提醒」共用。
 */

const FREQ_UNITS = [
  { value: 'daily', label: '天' },
  { value: 'weekly', label: '週' },
  { value: 'monthly', label: '月' },
];

const freqLabel = (frequency, value) =>
  `每${value > 1 ? ` ${value} ` : ''}${FREQ_UNITS.find(u => u.value === frequency)?.label || '週'}`;

/**
 * 由 assignment 建立編輯器初始狀態
 */
export function buildReminderState(assignment) {
  const schedule = assignment?.reminder_schedule;
  const hasSchedule = Array.isArray(schedule) && schedule.length > 0;

  const segments = hasSchedule
    ? schedule
        .filter(s => !s.overdue)
        .map(s => ({
          end: s.end_days_before ?? 0,
          frequency: s.frequency || 'weekly',
          value: s.frequency_value || 1,
        }))
        .sort((a, b) => b.end - a.end)
    : [{ end: 0, frequency: assignment?.frequency || 'weekly', value: assignment?.frequency_value || 1 }];

  const overdueRaw = hasSchedule ? schedule.find(s => s.overdue) : null;

  return {
    deadline: assignment?.deadline ? new Date(assignment.deadline).toISOString().split('T')[0] : '',
    reminderDays: assignment?.reminder_days_before ?? 1,
    remindOnDeadline: assignment?.remind_on_deadline ?? true,
    periodicEnabled: assignment?.reminder_enabled ?? false,
    segments,
    overdueEnabled: !!overdueRaw,
    overdueFrequency: overdueRaw?.frequency || 'daily',
    overdueValue: overdueRaw?.frequency_value || 1,
    overdueStopDays: overdueRaw?.stop_after_days || 14,
  };
}

/**
 * 編輯器狀態 → API payload
 * 單一區段且無逾期段 → 舊格式（無截止日也可用）；否則 → reminder_schedule 分段格式
 * @param {object} state - 編輯器狀態
 * @param {object} options - { includeDeadline: false 時不帶 deadline 欄位（批量模式保留各廠商原截止日，
 *                            分段所需的截止日改由後端以各廠商既有截止日逐筆驗證）}
 * @return {{payload?: object, error?: string}}
 */
export function reminderStateToPayload(state, { includeDeadline = true } = {}) {
  const base = {
    reminder_days_before: state.reminderDays,
    remind_on_deadline: state.remindOnDeadline,
    reminder_enabled: state.periodicEnabled,
    reminder_type: state.periodicEnabled ? 'periodic' : null,
  };
  if (includeDeadline) {
    base.deadline = state.deadline || null;
  }

  if (!state.periodicEnabled) {
    return { payload: { ...base, reminder_schedule: null } };
  }

  const isSimple = state.segments.length === 1 && !state.overdueEnabled;
  if (isSimple) {
    return {
      payload: {
        ...base,
        frequency: state.segments[0].frequency,
        frequency_value: state.segments[0].value,
        reminder_schedule: null,
      },
    };
  }

  // 分段格式需要截止日（批量不帶截止日時由後端以各廠商既有截止日驗證）
  if (includeDeadline && !state.deadline) {
    return { error: '分段提醒需要先設定截止日期' };
  }

  // 驗證分界嚴格遞減且 >= 0
  const ends = state.segments.map(s => Number(s.end));
  for (let i = 0; i < ends.length; i++) {
    if (!Number.isInteger(ends[i]) || ends[i] < 0) {
      return { error: '區段分界天數必須為 0 以上的整數' };
    }
    if (i > 0 && ends[i] >= ends[i - 1]) {
      return { error: '區段分界天數必須由遠至近遞減，不可重複' };
    }
  }

  const schedule = state.segments.map(s => ({
    end_days_before: Number(s.end),
    frequency: s.frequency,
    frequency_value: Number(s.value) || 1,
  }));

  if (state.overdueEnabled) {
    if (!Number.isInteger(Number(state.overdueStopDays)) || Number(state.overdueStopDays) < 1) {
      return { error: '逾期提醒必須設定停止天數（1 以上）' };
    }
    schedule.push({
      overdue: true,
      frequency: state.overdueFrequency,
      frequency_value: Number(state.overdueValue) || 1,
      stop_after_days: Number(state.overdueStopDays),
    });
  }

  return { payload: { ...base, reminder_schedule: schedule } };
}

/**
 * 表格欄位的精簡顯示文字，例如「每2週 → 每天」或「每週」
 */
export function describeReminder(assignment) {
  if (!assignment?.reminder_enabled) return null;
  const schedule = assignment.reminder_schedule;
  if (Array.isArray(schedule) && schedule.length > 0) {
    const parts = schedule
      .filter(s => !s.overdue)
      .sort((a, b) => (b.end_days_before ?? 0) - (a.end_days_before ?? 0))
      .map(s => freqLabel(s.frequency, s.frequency_value || 1));
    const overdue = schedule.find(s => s.overdue);
    if (overdue) parts.push(`逾期${freqLabel(overdue.frequency, overdue.frequency_value || 1)}`);
    return parts.join(' → ');
  }
  return freqLabel(assignment.frequency || 'weekly', assignment.frequency_value || 1);
}

/** 依頻率推算下一個日期（與後端 reminderEngine 一致） */
function addInterval(date, frequency, value) {
  const next = new Date(date);
  if (frequency === 'daily') next.setDate(next.getDate() + value);
  else if (frequency === 'weekly') next.setDate(next.getDate() + 7 * value);
  else if (frequency === 'monthly') next.setMonth(next.getMonth() + value);
  return next;
}

/**
 * 模擬提醒寄送日（與後端 reminderEngine 同邏輯：分段、同日去重、逾期停止）
 * @return {Map<dateKey, 'deadline'|'key'|'periodic'>} dateKey = YYYY-M-D
 */
function simulateSendDays(state) {
  const marks = new Map();
  if (!state.deadline) return marks;

  const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadline = new Date(state.deadline + 'T00:00:00');

  const segments = [...state.segments]
    .map(s => ({ end: Number(s.end) || 0, frequency: s.frequency, value: Number(s.value) || 1 }))
    .sort((a, b) => b.end - a.end);

  const endDate = new Date(deadline);
  if (state.overdueEnabled) endDate.setDate(endDate.getDate() + Number(state.overdueStopDays || 0));

  // 模擬上限 180 天，避免極端設定卡住畫面
  const cap = new Date(today);
  cap.setDate(cap.getDate() + 180);

  let anchor = new Date(today); // 假設今天儲存設定
  const day = new Date(today);
  day.setDate(day.getDate() + 1);

  for (; day <= endDate && day <= cap; day.setDate(day.getDate() + 1)) {
    const daysUntil = Math.round((deadline - day) / 86400000);

    // 關鍵日期提醒（單發）優先
    if (daysUntil === 0 && state.remindOnDeadline) {
      marks.set(dayKey(day), 'deadline');
      anchor = new Date(day);
      continue;
    }
    if (daysUntil === state.reminderDays && state.reminderDays > 0) {
      marks.set(dayKey(day), 'key');
      anchor = new Date(day);
      continue;
    }

    if (!state.periodicEnabled) continue;

    // 找當前區段
    let seg = null;
    if (daysUntil < 0) {
      if (state.overdueEnabled && -daysUntil <= Number(state.overdueStopDays || 0)) {
        seg = { frequency: state.overdueFrequency, value: Number(state.overdueValue) || 1 };
      }
    } else {
      seg = segments.find(s => daysUntil > s.end) || segments[segments.length - 1];
    }
    if (!seg) continue;

    if (addInterval(anchor, seg.frequency, seg.value) <= day) {
      marks.set(dayKey(day), 'periodic');
      anchor = new Date(day);
    }
  }
  return marks;
}

/**
 * 行事曆預覽：標出模擬的寄信日，設定好壞一目瞭然
 */
function ReminderPreviewCalendar({ state }) {
  if (!state.deadline) {
    return <p className="text-xs text-gray-400">設定截止日期後，這裡會顯示寄信日預覽</p>;
  }

  const marks = simulateSendDays(state);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadline = new Date(state.deadline + 'T00:00:00');
  const endDate = new Date(deadline);
  if (state.overdueEnabled) endDate.setDate(endDate.getDate() + Number(state.overdueStopDays || 0));

  // 要顯示的月份（今天所在月 ～ 結束月，最多 4 個月）
  const months = [];
  const cursor = new Date(today.getFullYear(), today.getMonth(), 1);
  while (cursor <= endDate && months.length < 4) {
    months.push(new Date(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const deadlineKey = dayKey(deadline);
  const todayKey = dayKey(today);

  const markStyle = {
    deadline: 'bg-red-500 text-white font-bold',
    key: 'bg-amber-400 text-white font-bold',
    periodic: 'bg-purple-500 text-white',
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        <span><span className="inline-block w-3 h-3 rounded bg-purple-500 align-middle mr-1" />定期催辦</span>
        <span><span className="inline-block w-3 h-3 rounded bg-amber-400 align-middle mr-1" />截止前提醒</span>
        <span><span className="inline-block w-3 h-3 rounded bg-red-500 align-middle mr-1" />截止當天</span>
        <span className="text-gray-400">共 {marks.size} 封</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {months.map((monthStart) => {
          const year = monthStart.getFullYear();
          const month = monthStart.getMonth();
          const firstWeekday = new Date(year, month, 1).getDay();
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const cells = [
            ...Array.from({ length: firstWeekday }, () => null),
            ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
          ];
          return (
            <div key={`${year}-${month}`} className="bg-white rounded border border-gray-200 p-2">
              <p className="text-xs font-medium text-gray-700 text-center mb-1">{year}/{month + 1}</p>
              <div className="grid grid-cols-7 gap-0.5 text-center">
                {['日', '一', '二', '三', '四', '五', '六'].map(w => (
                  <span key={w} className="text-[10px] text-gray-400">{w}</span>
                ))}
                {cells.map((d, i) => {
                  if (!d) return <span key={`e${i}`} />;
                  const key = `${year}-${month}-${d}`;
                  const mark = marks.get(key);
                  const isDeadline = key === deadlineKey;
                  const isToday = key === todayKey;
                  return (
                    <span
                      key={key}
                      className={`text-[10px] leading-4 h-4 rounded ${
                        mark ? markStyle[mark] :
                        isDeadline ? 'ring-1 ring-red-400 text-red-600' :
                        isToday ? 'ring-1 ring-purple-400 text-purple-600' :
                        'text-gray-500'
                      }`}
                      title={mark === 'periodic' ? '定期催辦' : mark === 'key' ? '截止前提醒' : mark === 'deadline' ? '截止當天提醒' : ''}
                    >
                      {d}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-gray-400">預覽以「今天儲存設定」推算；廠商完成填寫後即停止提醒</p>
    </div>
  );
}

/**
 * 提醒設定編輯器（受控元件）
 * @param {object} state - buildReminderState 產生的狀態
 * @param {function} onChange - (newState) => void
 * @param {boolean} showDeadline - 是否顯示截止日期欄（批量模式可隱藏以保留各廠商原截止日）
 */
export default function ReminderScheduleEditor({ state, onChange, showDeadline = true }) {
  const set = (patch) => onChange({ ...state, ...patch });

  const setSegment = (idx, patch) => {
    const segments = state.segments.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    set({ segments });
  };

  const addSegment = () => {
    // 在最前面插入一個更遠的區段，分界預設為現有最大分界 + 7
    const maxEnd = Math.max(...state.segments.map(s => Number(s.end) || 0));
    const segments = [
      { end: maxEnd + 7, frequency: 'weekly', value: 2 },
      ...state.segments,
    ];
    set({ segments });
  };

  const removeSegment = (idx) => {
    if (state.segments.length <= 1) return;
    set({ segments: state.segments.filter((_, i) => i !== idx) });
  };

  // ===== 絕對日期 ↔ 相對天數換算（有截止日時區段用日期選擇器呈現）=====
  const deadlineDate = state.deadline ? new Date(state.deadline + 'T00:00:00') : null;
  const useAbsoluteDates = showDeadline && !!deadlineDate;

  const toDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // 區段分界（截止日前 end 天）對應的絕對日期
  const endToDate = (end) => {
    const d = new Date(deadlineDate);
    d.setDate(d.getDate() - (Number(end) || 0));
    return toDateStr(d);
  };

  // 使用者選日期 → 換算回「截止日前幾天」
  const dateToEnd = (dateStr) => {
    const picked = new Date(dateStr + 'T00:00:00');
    return Math.round((deadlineDate - picked) / 86400000);
  };

  const displayDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  // 每個區段的起始說明（上一段的分界日期或「即日起」）
  const segmentStartLabel = (idx) => {
    if (idx === 0) return '即日起';
    return displayDate(endToDate(state.segments[idx - 1].end));
  };

  return (
    <div className="space-y-4">
      {/* 截止日期 */}
      {showDeadline && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">截止日期</label>
          <input
            type="date"
            value={state.deadline}
            onChange={(e) => set({ deadline: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          />
        </div>
      )}

      {/* 關鍵日期提醒（單發） */}
      <div className="p-3 bg-amber-50 rounded-lg space-y-2">
        <p className="text-sm font-medium text-amber-800">關鍵日期提醒（單發）</p>
        <div className="flex items-center gap-3 flex-wrap">
          {useAbsoluteDates ? (
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={state.reminderDays > 0}
                  onChange={(e) => set({ reminderDays: e.target.checked ? 7 : 0 })}
                  className="rounded text-purple-600"
                />
                <span className="text-sm text-gray-600">截止前提醒，於</span>
              </label>
              {state.reminderDays > 0 && (
                <>
                  <input
                    type="date"
                    value={endToDate(state.reminderDays)}
                    min={toDateStr(new Date())}
                    max={endToDate(1)}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      set({ reminderDays: Math.max(1, dateToEnd(e.target.value)) });
                    }}
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-xs text-gray-500">（截止前 {state.reminderDays} 天）</span>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">截止前</span>
              <input
                type="number"
                min="0"
                max="60"
                value={state.reminderDays}
                onChange={(e) => set({ reminderDays: parseInt(e.target.value) || 0 })}
                className="w-16 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-600">天提醒（0 = 關閉）</span>
            </div>
          )}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={state.remindOnDeadline}
              onChange={(e) => set({ remindOnDeadline: e.target.checked })}
              className="rounded text-purple-600"
            />
            <span className="text-sm text-gray-600">截止當天提醒</span>
          </label>
        </div>
      </div>

      {/* 定期催辦（分段頻率） */}
      <div className="p-3 bg-purple-50 rounded-lg space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={state.periodicEnabled}
            onChange={(e) => set({ periodicEnabled: e.target.checked })}
            className="rounded text-purple-600"
          />
          <span className="text-sm font-medium text-purple-700">啟用定期催辦</span>
        </label>

        {state.periodicEnabled && (
          <>
            <div className="space-y-2">
              {state.segments.map((seg, idx) => (
                <div key={idx} className="flex items-center gap-2 flex-wrap bg-white rounded p-2 border border-purple-100">
                  {useAbsoluteDates ? (
                    Number(seg.end) !== 0 ? (
                      <>
                        <span className="text-xs text-gray-600">{segmentStartLabel(idx)} ～</span>
                        <input
                          type="date"
                          value={endToDate(seg.end)}
                          min={toDateStr(new Date())}
                          max={endToDate(1)}
                          onChange={(e) => {
                            if (!e.target.value) return;
                            setSegment(idx, { end: dateToEnd(e.target.value) });
                          }}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                        />
                      </>
                    ) : (
                      <span className="text-xs text-gray-600">
                        {segmentStartLabel(idx)} ～ 截止日 {displayDate(state.deadline)}
                      </span>
                    )
                  ) : Number(seg.end) !== 0 ? (
                    <>
                      <span className="text-xs text-gray-600">{idx === 0 ? '截止日前' : '～截止日前'}</span>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={seg.end}
                        onChange={(e) => setSegment(idx, { end: parseInt(e.target.value) || 0 })}
                        className="w-14 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                      />
                      <span className="text-xs text-gray-600">{idx === 0 ? '天之前' : '天'}</span>
                    </>
                  ) : (
                    <span className="text-xs text-gray-600">
                      {idx > 0 ? `截止日前 ${state.segments[idx - 1].end}～0 天` : '直到截止日'}
                    </span>
                  )}
                  <span className="text-xs text-gray-500 ml-auto">每</span>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={seg.value}
                    onChange={(e) => setSegment(idx, { value: parseInt(e.target.value) || 1 })}
                    className="w-12 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                  />
                  <select
                    value={seg.frequency}
                    onChange={(e) => setSegment(idx, { frequency: e.target.value })}
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                  >
                    {FREQ_UNITS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                  {state.segments.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSegment(idx)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="移除此區段"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addSegment}
              className="text-xs text-purple-600 hover:text-purple-800 underline"
            >
              ＋ 新增區段（距截止日較遠時用不同頻率）
            </button>

            {showDeadline && state.segments.length > 1 && !state.deadline && (
              <p className="text-xs text-red-600">分段提醒需要設定截止日期</p>
            )}

            {/* 逾期追蹤 */}
            <div className="pt-2 border-t border-purple-100">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={state.overdueEnabled}
                  onChange={(e) => set({ overdueEnabled: e.target.checked })}
                  className="rounded text-purple-600"
                />
                <span className="text-xs font-medium text-gray-700">逾期後繼續提醒</span>
              </label>
              {state.overdueEnabled && (
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <span className="text-xs text-gray-600">每</span>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={state.overdueValue}
                    onChange={(e) => set({ overdueValue: parseInt(e.target.value) || 1 })}
                    className="w-12 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                  />
                  <select
                    value={state.overdueFrequency}
                    onChange={(e) => set({ overdueFrequency: e.target.value })}
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                  >
                    {FREQ_UNITS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                  {useAbsoluteDates ? (
                    <>
                      <span className="text-xs text-gray-600">，追蹤至</span>
                      <input
                        type="date"
                        value={endToDate(-Number(state.overdueStopDays || 1))}
                        min={endToDate(-1)}
                        onChange={(e) => {
                          if (!e.target.value) return;
                          const days = -dateToEnd(e.target.value);
                          set({ overdueStopDays: Math.max(1, days) });
                        }}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                      />
                    </>
                  ) : (
                    <>
                      <span className="text-xs text-gray-600">，逾期</span>
                      <input
                        type="number"
                        min="1"
                        max="90"
                        value={state.overdueStopDays}
                        onChange={(e) => set({ overdueStopDays: parseInt(e.target.value) || 1 })}
                        className="w-14 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                      />
                      <span className="text-xs text-gray-600">天後停止</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* 行事曆預覽 */}
      {showDeadline && (
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-700 mb-2">📆 寄信日預覽</p>
          <ReminderPreviewCalendar state={state} />
        </div>
      )}

      <p className="text-xs text-gray-500">
        已填寫的廠商會自動跳過所有提醒；同一天多種提醒重疊時只寄一封（截止提醒優先）。
      </p>
    </div>
  );
}
